import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { useApplyPhaseTemplate } from "../../../hooks/annual-planner/useApplyPhaseTemplate";
import { useHorizontalScroll } from "../../../hooks/useHorizontalScroll";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { getPhaseTemplateStatus } from "../../../utils/annual-planner-helpers";
import Modal from "../../modal-new";
import ConfirmationModal from "../ConfirmationModal";
import { PhaseTemplateCard } from "./parts/PhaseTemplateCard";

export const PhaseTemplatesModal = ({ athleteId, organizationCoachId }) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const { locals } = useRillContext();

  const {
    phasesTemplates: { defaultTemplate = undefined, templates = [] },
    athlete,
  } = locals;

  // `athlete.annualPlan` is already normalized server-side (see
  // ensure-coach.js) - no local transform needed.
  const { phases = [] } = athlete?.annualPlan?.trainingPhases || {};

  const { currentTemplateId, defaultTemplateObject } = getPhaseTemplateStatus({
    athlete,
    templates,
    defaultTemplate,
  });

  const currentPhaseTemplateIndex = templates.findIndex(
    (phase) => phase._id === currentTemplateId
  );

  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState(
    currentPhaseTemplateIndex
  );
  const [saveDefaultTemplate, setSaveDefaultTemplate] = useState(false);

  const {
    pendingTemplate,
    requestApplyTemplate,
    confirmPendingTemplate,
    cancelPendingTemplate,
    setAsDefaultTemplate,
  } = useApplyPhaseTemplate({
    athleteId,
    organizationCoachId,
    hasDatedPhases: !!phases.length,
  });

  const elRef = useHorizontalScroll("faster");

  const openAddPhaseTemplateModal = () => {
    submit("/actions/refresh", {
      method: "GET",
      body: { "modal[type]": "add-phase-template" },
    });
  };

  const openRemoveDefaultTemplateModal = () => {
    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "remove-default-template",
        defaultTemplateName: defaultTemplateObject.templateName,
        defaultTemplateId: defaultTemplateObject._id,
      },
    });
  };

  // The selected template is already the athlete's current one - there are
  // no phases to (re)apply, so this only persists the "set as default"
  // checkbox if it was toggled, or just closes the modal untouched.
  const keepCurrentTemplate = () => {
    if (!saveDefaultTemplate) {
      return submit("/actions/refresh", {
        method: "GET",
        body: { "modal[type]": "" },
      });
    }

    setAsDefaultTemplate(templates[selectedPhaseIndex]);
  };

  const handleSaveClick = () => {
    const isSameTemplate =
      templates[selectedPhaseIndex] &&
      templates[selectedPhaseIndex]._id === currentTemplateId;

    if (isSameTemplate) return keepCurrentTemplate();
    requestApplyTemplate(templates[selectedPhaseIndex], {
      saveDefaultTemplate,
    });
  };

  if (pendingTemplate) {
    return (
      <ConfirmationModal
        isOpen
        className="annual-planner-modal phase-templates-modal warning"
        badgeText={t("phaseTemplatesModal.warningLabel")}
        title={t("phaseTemplatesModal.changeTemplateTitle")}
        contentText={t("phaseTemplatesModal.warningMessage")}
        cancelButtonText={t("common:cancel")}
        cancelButtonAction={cancelPendingTemplate}
        confirmButtonText={t("phaseTemplatesModal.changeButton")}
        confirmButtonAction={confirmPendingTemplate}
        confirmSuccess
      />
    );
  }

  return (
    <Modal
      className="annual-planner-modal phase-templates-modal"
      title={t("phaseTemplatesModal.title")}
    >
      <div className="body">
        <div className="phase-templates" ref={elRef}>
          {templates.map((template, index) => {
            return (
              <PhaseTemplateCard
                key={`temps-phase-card-${index}`}
                template={template}
                index={index}
                selectedPhaseIndex={selectedPhaseIndex}
                setSelectedPhaseIndex={setSelectedPhaseIndex}
              />
            );
          })}
        </div>
      </div>
      <footer className="footer">
        <div className="footer-left">
          <button
            role="button"
            data-button-type="outline"
            onClick={openAddPhaseTemplateModal}
          >
            {t("phaseTemplatesModal.addNewTemplate")}
          </button>
          <div
            className="set-as-default-wrapper"
            onClick={() => setSaveDefaultTemplate(!saveDefaultTemplate)}
          >
            <div
              className={`set-as-default-checkbox${
                saveDefaultTemplate ? " checked" : ""
              }`}
            >
              {saveDefaultTemplate && <img src="/img/svg/check.svg" />}
            </div>
            <label>{t("phaseTemplatesModal.setAsDefault")}</label>
          </div>
          {defaultTemplate && (
            <div className="show-default-wrapper">
              <img
                src="/img/new-icons/red-trash.svg"
                className="icon-remove-default"
                onClick={openRemoveDefaultTemplateModal}
              />
              <label>
                {defaultTemplateObject.templateName}{" "}
                <span style={{ color: "var(--white-20)" }}>
                  {t("phaseTemplatesModal.defaultLabel")}
                </span>
              </label>
            </div>
          )}
        </div>
        <div className="footer-right">
          <button
            disabled={!templates[selectedPhaseIndex]}
            onClick={handleSaveClick}
            role="button"
            data-button-type="primary"
          >
            {t("common:save")}
          </button>
        </div>
      </footer>
    </Modal>
  );
};
